"use client"

import { useMemo } from "react"
import { Check } from "lucide-react"
import { cn } from "@/lib/utils"
import { useEngagementNavigation } from "@/contexts/engagement-navigation-context"
import { useFormNavigationItems } from "@/hooks/use-form-navigation-items"

interface FormStepperProps {
  completedSections: Record<string, boolean>
  hiddenSections: Record<string, boolean>
  enableCompletionLineColor?: boolean
}

// Get colors for a step based on complete/active state
function getStepColors(isComplete: boolean, isActive: boolean) {
  // Color logic: Complete = SUCCESS, Incomplete = (Active: PRIMARY, Inactive: GRAY)
  if (isComplete) {
    return {
      border: "border-success",
      bg: "bg-success",
      text: "text-success"
    }
  } else if (isActive) {
    return {
      border: "border-primary",
      bg: "bg-primary",
      text: "text-primary"
    }
  } else {
    return {
      border: "border-gray-400",
      bg: "bg-gray-400",
      text: "text-gray-500"
    }
  }
}

function StepCircle({ index, isComplete, isActive }: { index: number; isComplete: boolean; isActive: boolean }) {
  // Style: Active = small circle with ring, Inactive = large circle no ring
  const colors = getStepColors(isComplete, isActive)
  
  if (isActive) {
    // Active: Small circle with border ring
    return (
      <div className="relative w-6 h-6 flex items-center justify-center">
        <div className={cn("absolute inset-0 rounded-full border-2", colors.border)} />
        <div className={cn("w-4 h-4 rounded-full flex items-center justify-center", colors.bg)}>
          {isComplete ? (
            <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
          ) : (
            <span className="text-xs font-normal text-white leading-none">
              {index + 1}
            </span>
          )}
        </div>
      </div>
    )
  } else {
    // Inactive: Large circle, no ring
    return (
      <div className={cn("w-6 h-6 rounded-full flex items-center justify-center", colors.bg)}>
        {isComplete ? (
          <Check className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
        ) : (
          <span className="text-xs font-normal text-white leading-none">
            {index + 1}
          </span>
        )}
      </div>
    )
  }
}

export function FormStepper({
  completedSections,
  hiddenSections,
  enableCompletionLineColor = false
}: FormStepperProps) {
  const { goToSection } = useEngagementNavigation()
  const items = useFormNavigationItems({ completedSections, hiddenSections })
  
  // Find the index of the last completed step
  const lastCompletedIndex = useMemo(() => {
    return items.reduce((lastIndex, item, index) => {
      return item.completed ? index : lastIndex
    }, -1)
  }, [items])

  return (
    <div className="flex justify-center">
      <nav className="overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent max-w-full" role="navigation" aria-label="Form steps">
        <div className="flex items-center min-w-max px-2 py-1">
          {items.map((item, index) => {
          const stepState = item.completed ? "completed" : "incomplete"
          const ariaLabel = `${item.label}, step ${index + 1} of ${items.length}, ${stepState}`
          
          return (
            <div key={item.id} className="flex items-center">
              <button
                onClick={() => goToSection(item.id)}
                className={cn(
                  "flex items-center px-2 py-1.5 gap-1.5 shrink-0 rounded-lg transition-colors hover:bg-muted",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                  item.active ? "pointer-events-none" : ""
                )}
                aria-label={ariaLabel}
                aria-current={item.active ? "step" : undefined}
              >
                {/* Circle/Icon */}
                <StepCircle index={index} isComplete={item.completed} isActive={item.active} />

                {/* Label */}
                <span
                  className={cn(
                    "text-sm transition-colors whitespace-nowrap",
                    getStepColors(item.completed, item.active).text,
                    item.active ? "font-semibold" : "font-normal"
                  )}
                >
                  {item.label}
                </span>
              </button>

              {/* Connecting Line */}
              {index < items.length - 1 && (
                <div className="relative w-12 h-px mx-1.5 bg-gray-400 shrink-0">
                  <div
                    className={cn(
                      "absolute left-0 top-0 h-px bg-success transition-all duration-300",
                      index < lastCompletedIndex && enableCompletionLineColor ? "w-full" : "w-0"
                    )}
                  />
                </div>
              )}
            </div>
          )
        })}
        </div>
      </nav>
    </div>
  )
}
